/**
 * Created by WorkAxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev.e@workaxis.in
 * Admin: shyam@workaxis.in
 * Date: 25/08/22
 * Time: 2:39 PM
 * Project: W-CRM
 */
import React, {useEffect, useMemo, useState} from "react";
import "./style.css";
import NavigationVerticalItem from "./Components/NavigationVerticalItem";
import {useLocation, useNavigate} from "react-router-dom";
import { useSelector } from "react-redux";
import TopNavigation from "./Components/TopNavigation";
import {generateBreadCrumbs} from "../../utils/common";
import {isPrivilegeAllowed} from "../../utils/PrivilegeControl/PrivilegeControl";
import { authState } from "../../redux/auth/authReducer";
import { profileState } from "../../redux/profile/profileReducer";
import { useBrand } from "../../context/BrandContext";
import { getActiveRouteConfig, getConfiguredRoutes } from "../../routes/routesConfig";
import { isAiFeaturesEnabledForOrg } from "../../utils/constants";

export default function NavigationController({children}) {
    const brand = useBrand();
    const { isAuthenticated } = useSelector(authState);
    const { profile } = useSelector(profileState);
    const orgId = profile?.data?.userorgrolemapping?.organization?.id;
    const aiFeaturesEnabled = isAiFeaturesEnabledForOrg(orgId);
    const activeRouteConfig = useMemo(() => getActiveRouteConfig({
        isAuthenticated,
        profileRouteConfig: profile?.data?.route_config,
        brandRouteConfig: brand?.route_config,
    }), [brand?.route_config, isAuthenticated, profile?.data?.route_config]);
    const routes = useMemo(() => getConfiguredRoutes(activeRouteConfig), [activeRouteConfig]);
    const [navExpand, setNavExpand] = useState(true);
    const [mobileNavExpand, setMobileNavExpand] = useState(false);
    let navigate = useNavigate();
    let location = useLocation();
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    useEffect(() => {
        setBreadcrumbs(generateBreadCrumbs(location.pathname, routes.private));
    }, [location.pathname, routes.private]);

    const isNavItemVisible = (value) => {
        let isSelected = value.showOnNav !== false;
        if (value.notAllowed && isSelected) {
            isSelected = !isPrivilegeAllowed(value.notAllowed);
        }
        // AI features: always shown in nav when org helper allows (all orgs)
        if (
            isSelected &&
            ["ai_agent", "ai_campaigns", "ai_campaign_new", "ai_campaign_detail"].includes(value.id) &&
            !aiFeaturesEnabled
        ) {
            isSelected = false;
        }
        return isSelected;
    };

    return (
        <div className={"navigation-parent-container"}>
            <div className={"navigation-top-nav-container"}>
                <TopNavigation
                    mobileNavExpand={mobileNavExpand}
                    mobileNavCallback={() => {
                        setMobileNavExpand((old) => !old);
                    }}
                />
            </div>
            <div className={"navigation-body-container"}>
                <div
                    className={
                        "navigation-left-nav-container " + (navExpand ? "" : "collapsed")
                    }
                >
                    <div className={"navigation-vertical-item-container"}>
                        {routes.private
                            .filter(isNavItemVisible)
                            .map((value) => {
                                const _isActive = breadcrumbs.some(
                                    (i) => i.actualPath === value.path
                                );
                                return (
                                    <NavigationVerticalItem
                                        nav={value}
                                        key={value.path}
                                        isActive={
                                            _isActive ||
                                            (value.path === "/" && breadcrumbs.length === 0)
                                        }
                                        onSelect={() => {
                                            navigate(value.path);
                                        }}
                                    />
                                );
                            })}
                    </div>
                </div>

                <div
                    className={"navigation-vertical-toggle-holder clickable"}
                    onClick={() => setNavExpand((old) => !old)}
                >
                    <div className={"wx-icons"}>arrow_forward_ios</div>
                </div>

                <div
                    className={
                        "navigation-right-nav-container onlyNavMobile " +
                        (mobileNavExpand ? "" : "collapsed")
                    }
                >
                    <div className={"mobile-navigation-holder"}>
                        <div className={"mobile-navigation-header-holder"}>
                            <div
                                className={"mobile-navigation-toggle-holder clickable"}
                                onClick={() => {
                                    setMobileNavExpand((old) => !old);
                                }}
                            >
                                {mobileNavExpand ? (
                                    <div className={"wx-icons"}>close</div>
                                ) : (
                                    <div className={"wx-icons"}>menu</div>
                                )}
                            </div>
                            <img
                                src={brand?.logo_mobile}
                                alt={`${brand?.app_name}-logo`}
                                className={"mobile-navigation-brand-icon"}
                            />
                        </div>
                        <div className={"mobile-navigation-item-holder"}>
                            {routes.private.map((nav) => {
                                const {icon: Icon} = nav;
                                const isActive = breadcrumbs.some(
                                    (i) => i.actualPath === nav.path
                                );
                                let css = "mobile-navigation-item clickable";
                                if (isActive) {
                                    css = `${css} active`;
                                }
                                const shouldShow = nav.showOnMobile !== undefined ? nav.showOnMobile : nav.showOnNav;
                                if (shouldShow) {
                                    return (
                                        <div
                                            className={css}
                                            onClick={() => {
                                                navigate(nav.path);
                                                setMobileNavExpand(false);
                                            }}
                                            key={nav.name}
                                        >
                                            <Icon
                                                className={"mobile-nav-icon"}
                                                strokeWidth={isActive ? 2.5 : 2}
                                            />
                                            <span className={"text"}>{nav.name}</span>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    </div>
                </div>
                <div className={"navigation-page-container"}>{children}</div>
            </div>
        </div>
    );
}
